import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import {
  NAME_MAX,
  classificationLookupFormSchema,
  type ClassificationLookupFormValues,
} from './formSchema'
import type { ClassificationLookup, ClassificationLookupUpsert } from '../types'

interface Props {
  visible: boolean
  initial: ClassificationLookup | null
  submitting: boolean
  /** Ekran metinlerinin i18n öneki (`config.ts` → `i18nPrefix`). */
  i18nPrefix: string
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: ClassificationLookupUpsert) => void
  onHide: () => void
}

/**
 * Stratejik hedef / katkı ekle-düzenle formu (127: react-hook-form + zod). TR ad zorunlu; EN opsiyonel;
 * ikisi de 255 karakter / 255 bayt. Sıra formda yok. (`EventTypeFormDialog` deseniyle aynı.)
 */
export default function ClassificationLookupFormDialog({
  visible,
  initial,
  submitting,
  i18nPrefix,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(classificationLookupFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: ClassificationLookupFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t(`${i18nPrefix}.form.editTitle`) : t(`${i18nPrefix}.form.newTitle`)}
      submitLabel={t(`${i18nPrefix}.form.save`)}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t(`${i18nPrefix}.form.nameTr`)}
          required
          autoFocus
          maxLength={NAME_MAX}
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t(`${i18nPrefix}.form.nameEn`)}
          maxLength={NAME_MAX}
        />
      </div>
    </FormDialog>
  )
}
